import {escapeHtml as escapePrintValue} from "../../utils/escapeHtml";
import {calculateFoodPending} from "../../services/billingService";
import React, {
  useMemo,
  useState,
} from "react";

import {
  roundMoney,
  toAmount,
} from "../../services/billingService";

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const formatMoney = (value) =>
  `₹${toAmount(value).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;

const getDateObject = (value) => {
  if (!value) return null;

  if (
    typeof value?.toDate ===
    "function"
  ) {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(
      value.seconds * 1000
    );
  }

  const date = new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? null
    : date;
};

const formatDateTime = (
  value
) => {
  const date =
    getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

function FoodBillModal({
  stay,
  hotelName,
  saving = false,
  onClose,
  onReceivePayment,
}) {
  const items = useMemo(
    () =>
      Array.isArray(
        stay?.foodItems
      )
        ? stay.foodItems
        : [],
    [stay]
  );

  const foodTotal =
    roundMoney(
      toAmount(
        stay?.foodTotal
      )
    );

  const foodPaid =
    roundMoney(
      toAmount(
        stay?.foodPaidTotal
      )
    );

  const pending = calculateFoodPending(stay);

  const [
    paymentAmount,
    setPaymentAmount,
  ] = useState(
    pending > 0
      ? String(pending)
      : ""
  );

  const [
    paymentMode,
    setPaymentMode,
  ] = useState("Cash");

  const [
    transactionReference,
    setTransactionReference,
  ] = useState("");

  const [note, setNote] =
    useState("");

  const [
    localSaving,
    setLocalSaving,
  ] = useState(false);

  const isSaving =
    saving || localSaving;

  const printFoodBill = () => {
    const popup = window.open(
      "",
      "_blank",
      "width=700,height=900"
    );

    if (!popup) {
      alert(
        "Browser popup block kar raha hai. Popups allow karo."
      );
      return;
    }

    const rows = items
      .map(
        (item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapePrintValue(clean(
              item.title ||
                item.itemName ||
                "Food Item"
            ))}</td>
            <td>${toAmount(
              item.quantity || 1
            )}</td>
            <td>${formatMoney(
              item.rate
            )}</td>
            <td>${formatMoney(
              item.amount
            )}</td>
            <td>${formatDateTime(
              item.addedAt
            )}</td>
          </tr>
        `
      )
      .join("");

    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Food Bill - ${
            stay?.room || ""
          }</title>
          <style>
            * { box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 18px;
              color: #111;
            }
            .sheet {
              max-width: 700px;
              margin: auto;
              border: 2px solid #111;
              padding: 14px;
            }
            h1, h2, p { margin: 0; }
            .center { text-align: center; }
            .meta {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              margin: 14px 0;
            }
            .meta div {
              border: 1px solid #aaa;
              padding: 7px;
              font-size: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #555;
              padding: 7px;
              font-size: 11px;
              vertical-align: top;
            }
            th { background: #eee; }
            .totals {
              margin-top: 12px;
              margin-left: auto;
              width: 300px;
            }
            .totals div {
              display: flex;
              justify-content: space-between;
              border-bottom: 1px solid #aaa;
              padding: 6px 0;
            }
            .pending {
              font-weight: 800;
              font-size: 16px;
            }
            .sign {
              margin-top: 45px;
              display: flex;
              justify-content: space-between;
              gap: 25px;
            }
            @page {
              size: A5 portrait;
              margin: 8mm;
            }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="center">
              <h1>${escapePrintValue(clean(
                hotelName ||
                  stay?.hotelName ||
                  "Hotel"
              ))}</h1>
              <h2>FOOD BILL</h2>
            </div>

            <div class="meta">
              <div>
                <b>Room:</b>
                ${escapePrintValue(clean(
                  stay?.room || "-"
                ))}
              </div>
              <div>
                <b>Guest:</b>
                ${escapePrintValue(clean(
                  stay?.guestName ||
                    "-"
                ))}
              </div>
              <div>
                <b>Check-In:</b>
                ${formatDateTime(
                  stay?.checkInAt
                )}
              </div>
              <div>
                <b>Printed:</b>
                ${formatDateTime(
                  new Date()
                )}
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Particulars</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Date / Time</th>
                </tr>
              </thead>
              <tbody>
                ${
                  rows ||
                  `<tr><td colspan="6" style="text-align:center">No food items</td></tr>`
                }
              </tbody>
            </table>

            <div class="totals">
              <div>
                <span>Total Food</span>
                <b>${formatMoney(
                  foodTotal
                )}</b>
              </div>

              <div>
                <span>Already Paid</span>
                <b>${formatMoney(
                  foodPaid
                )}</b>
              </div>

              <div class="pending">
                <span>Pending</span>
                <span>${formatMoney(
                  pending
                )}</span>
              </div>
            </div>

            <div class="sign">
              <div>
                Guest Signature
                <br/><br/>
                __________________
              </div>

              <div>
                Reception
                <br/><br/>
                __________________
              </div>
            </div>
          </div>

          <script>
            window.onload = () => {
              setTimeout(
                () => window.print(),
                300
              );
            };
          </script>
        </body>
      </html>
    `);

    popup.document.close();
  };

  const receiveFoodPayment =
    async () => {
      if (
        isSaving ||
        typeof onReceivePayment !==
          "function"
      ) {
        return;
      }

      const amount =
        roundMoney(
          toAmount(
            paymentAmount
          )
        );

      if (pending <= 0) {
        alert(
          "Food bill already clear hai."
        );
        return;
      }

      if (amount <= 0) {
        alert(
          "Valid payment amount enter karo."
        );
        return;
      }

      if (
        amount >
        pending + 0.01
      ) {
        alert(
          `Pending food bill ${formatMoney(
            pending
          )} hai.`
        );
        return;
      }

      if (
        paymentMode !== "Cash" &&
        !clean(
          transactionReference
        )
      ) {
        alert(
          "Online payment ka transaction code required hai."
        );
        return;
      }

      setLocalSaving(true);

      try {
        await onReceivePayment({
          type:
            "food_payment",
          title:
            "Food Bill Payment",
          quantity: 1,
          rate: amount,
          amount,
          paymentMode,
          transactionReference:
            clean(
              transactionReference
            ),
          note: clean(note),
          source:
            "food_bill_payment",
        });

        alert(
          "Food bill payment received ✅"
        );

        setPaymentAmount("");
        setTransactionReference("");
        setNote("");
      } catch (error) {
        console.error(
          "Food payment error:",
          error
        );

        alert(
          "Food payment save nahi hui: " +
            (error?.message ||
              error)
        );
      } finally {
        setLocalSaving(false);
      }
    };

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
            event.currentTarget &&
          !isSaving
        ) {
          onClose();
        }
      }}
    >
      <div style={modalStyle}>
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0 }}>
              🍴 Food Bill
            </h2>

            <div style={subTextStyle}>
              Room {stay?.room || "-"} •{" "}
              {stay?.guestName ||
                "Guest"}
            </div>
          </div>

          <button
            type="button"
            disabled={isSaving}
            onClick={onClose}
            style={closeButtonStyle}
          >
            ×
          </button>
        </div>

        <div style={summaryStyle}>
          <Summary
            label="Food Total"
            value={foodTotal}
          />

          <Summary
            label="Paid"
            value={foodPaid}
          />

          <Summary
            label="Pending"
            value={pending}
            danger={pending > 0}
          />
        </div>

        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>
                  #
                </th>
                <th style={thStyle}>
                  Date / Time
                </th>
                <th style={thStyle}>
                  Particular
                </th>
                <th style={thRightStyle}>
                  Qty
                </th>
                <th style={thRightStyle}>
                  Rate
                </th>
                <th style={thRightStyle}>
                  Amount
                </th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={emptyCellStyle}
                  >
                    Abhi koi food order
                    nahi hai.
                  </td>
                </tr>
              ) : (
                items.map(
                  (item, index) => (
                    <tr
                      key={
                        item.id ||
                        item.addedAt ||
                        index
                      }
                    >
                      <td style={tdStyle}>
                        {index + 1}
                      </td>

                      <td style={tdStyle}>
                        {formatDateTime(
                          item.addedAt
                        )}
                      </td>

                      <td style={tdStyle}>
                        <strong>
                          {item.title ||
                            item.itemName ||
                            "Food Item"}
                        </strong>

                        {item.note && (
                          <div
                            style={
                              noteStyle
                            }
                          >
                            {item.note}
                          </div>
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        {toAmount(
                          item.quantity ||
                            1
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        {formatMoney(
                          item.rate
                        )}
                      </td>

                      <td style={tdRightStyle}>
                        {formatMoney(
                          item.amount
                        )}
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          onClick={printFoodBill}
          style={printButtonStyle}
        >
          🖨 Print Food Bill
        </button>

        {pending > 0 ? (
          <section style={paymentBoxStyle}>
            <h3 style={{ marginTop: 0 }}>
              Receive Food Payment
            </h3>

            <div style={formGridStyle}>
              <label>
                <div style={labelStyle}>
                  Amount
                </div>

                <input
                  type="number"
                  min="0"
                  max={pending}
                  value={paymentAmount}
                  disabled={isSaving}
                  onChange={(event) =>
                    setPaymentAmount(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                />
              </label>

              <label>
                <div style={labelStyle}>
                  Payment Mode
                </div>

                <select
                  value={paymentMode}
                  disabled={isSaving}
                  onChange={(event) => {
                    const mode =
                      event.target.value;

                    setPaymentMode(mode);

                    if (mode === "Cash") {
                      setTransactionReference(
                        ""
                      );
                    }
                  }}
                  style={inputStyle}
                >
                  <option>
                    Cash
                  </option>
                  <option>
                    UPI
                  </option>
                  <option>
                    Card
                  </option>
                  <option>
                    Bank Transfer
                  </option>
                </select>
              </label>

              {paymentMode !==
                "Cash" && (
                <label>
                  <div style={labelStyle}>
                    Transaction Code
                  </div>

                  <input
                    value={
                      transactionReference
                    }
                    disabled={isSaving}
                    onChange={(event) =>
                      setTransactionReference(
                        event.target.value
                      )
                    }
                    style={inputStyle}
                    placeholder="Code / UTR"
                  />
                </label>
              )}

              <label>
                <div style={labelStyle}>
                  Note
                </div>

                <input
                  value={note}
                  disabled={isSaving}
                  onChange={(event) =>
                    setNote(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Optional"
                />
              </label>
            </div>

            <button
              type="button"
              disabled={isSaving}
              onClick={
                receiveFoodPayment
              }
              style={{
                ...receiveButtonStyle,
                opacity: isSaving
                  ? 0.6
                  : 1,
              }}
            >
              {isSaving
                ? "Saving..."
                : `Receive ${formatMoney(
                    toAmount(
                      paymentAmount
                    )
                  )}`}
            </button>
          </section>
        ) : (
          <div style={paidBoxStyle}>
            ✅ Food Bill Cleared
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,
          color: danger
            ? "#b91c1c"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 28000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 14,
  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(900px,100%)",
  maxHeight: "95vh",
  overflowY: "auto",
  background: "#fff",
  borderRadius: 18,
  padding: 18,
};

const headerStyle = {
  display: "flex",
  justifyContent:
    "space-between",
  gap: 12,
};

const subTextStyle = {
  marginTop: 5,
  color: "#64748b",
  fontSize: 12,
};

const closeButtonStyle = {
  width: 38,
  height: 38,
  border: 0,
  borderRadius: 9,
  fontSize: 24,
  cursor: "pointer",
};

const summaryStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(3,1fr)",
  gap: 9,
  marginTop: 14,
};

const summaryCardStyle = {
  padding: 11,
  border:
    "1px solid #e2e8f0",
  borderRadius: 10,
  textAlign: "center",
  background: "#f8fafc",
};

const summaryLabelStyle = {
  fontSize: 10,
  color: "#64748b",
  fontWeight: 800,
};

const summaryValueStyle = {
  marginTop: 5,
  fontSize: 17,
  fontWeight: 900,
};

const tableWrapperStyle = {
  marginTop: 14,
  overflowX: "auto",
  border:
    "1px solid #cbd5e1",
  borderRadius: 10,
};

const tableStyle = {
  width: "100%",
  minWidth: 700,
  borderCollapse: "collapse",
};

const thStyle = {
  padding: 9,
  borderBottom:
    "1px solid #cbd5e1",
  background: "#e2e8f0",
  fontSize: 10,
  textAlign: "left",
};

const thRightStyle = {
  ...thStyle,
  textAlign: "right",
};

const tdStyle = {
  padding: 9,
  borderBottom:
    "1px solid #e2e8f0",
  fontSize: 10,
  verticalAlign: "top",
};

const tdRightStyle = {
  ...tdStyle,
  textAlign: "right",
};

const emptyCellStyle = {
  ...tdStyle,
  padding: 25,
  textAlign: "center",
  color: "#64748b",
};

const noteStyle = {
  marginTop: 3,
  color: "#64748b",
  fontSize: 9,
};

const printButtonStyle = {
  width: "100%",
  marginTop: 12,
  padding: 11,
  border: 0,
  borderRadius: 9,
  background: "#0f766e",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const paymentBoxStyle = {
  marginTop: 14,
  padding: 14,
  border:
    "1px solid #fde68a",
  borderRadius: 12,
  background: "#fffbeb",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",
  gap: 10,
};

const labelStyle = {
  marginBottom: 6,
  color: "#334155",
  fontSize: 11,
  fontWeight: 800,
};

const inputStyle = {
  width: "100%",
  padding: 10,
  border:
    "1px solid #cbd5e1",
  borderRadius: 8,
  boxSizing: "border-box",
};

const receiveButtonStyle = {
  width: "100%",
  marginTop: 11,
  padding: 11,
  border: 0,
  borderRadius: 9,
  background: "#16a34a",
  color: "#fff",
  fontWeight: 900,
  cursor: "pointer",
};

const paidBoxStyle = {
  marginTop: 14,
  padding: 12,
  borderRadius: 10,
  background: "#dcfce7",
  color: "#166534",
  textAlign: "center",
  fontWeight: 900,
};

export default FoodBillModal;
